/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseSharesPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/shares 三方分配（spec §8.2 / 工程規格書 §6.3 ④sheet）—
 *                逐品 K = 落札単価 − メンバー調整後単価，比率 EF 20%／メンバー 40%／
 *                会場 40%，並做 P + Q + R = K 等式檢查（不一致 → 數字類報連相）。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  資料來源：
 *   - 已有 P2 run（GET /cases/:id/settlements/p2/latest）→ 取 run 內 per-item
 *     j / m / p / q / r（後端已校驗 P+Q+R=K，此處純展示）。
 *   - 無 run → 以 confirmed 落札単価（J）+ メンバー決策（M）+ 預設 20/40/40
 *     試算「預覽」（與後端 default_share 同演算法：整數単価，扣減保等式）。
 *  RBAC：讀取 = caseRead；分配實際提交在 P2 試算頁（無獨立 write endpoint）。
 *  ⚠️ 単価一律字串型 Decimal，前端不得轉 float 做金額累積（CLAUDE.md §2）。
 *  注：此頁僅 K/P/Q/R 整數単価層級的等式檢查與顯示，不做税込金額計算。
 */

import { useMemo, type ReactElement } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import { listAwards, type AwardDto } from '../api/auction'
import { listMemberDecisions, type MemberDecisionDto } from '../api/adjustments'
import { getLatestP2, type SecondSettlementRunDto } from '../api/settlements'
import { defaultShare } from '../lib/settlementShare'
import { ApiError } from '../api/client'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 三方分配展示列（整數単価層級）。 */
interface ShareRow {
  key: string
  label: string
  /** 落札単価 J。 */
  j: number
  /** メンバー調整後単価 M。 */
  m: number
  /** K = J − M。 */
  k: number
  p: number
  q: number
  r: number
}

function fmtNum(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('ja-JP', { maximumFractionDigits: 2 }) : '—'
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

export function CaseSharesPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const awardsQuery = useQuery({
    queryKey: ['case', caseId, 'awards'],
    queryFn: () => listAwards(caseId),
    enabled: caseId !== '',
  })
  const decisionsQuery = useQuery({
    queryKey: ['case', caseId, 'member-decisions'],
    queryFn: () => listMemberDecisions(caseId),
    enabled: caseId !== '',
  })
  const latestRunQuery = useQuery({
    queryKey: ['case', caseId, 'p2-latest'],
    queryFn: () => getLatestP2(caseId),
    enabled: caseId !== '',
  })

  const items = useMemo<CaseItemDto[]>(() => caseQuery.data?.items ?? [], [caseQuery.data])
  const awards = useMemo<AwardDto[]>(() => awardsQuery.data ?? [], [awardsQuery.data])
  const decisions = useMemo<MemberDecisionDto[]>(
    () => decisionsQuery.data ?? [],
    [decisionsQuery.data],
  )
  const latestRun = latestRunQuery.data ?? null

  /** 是否以 P2 run 為來源（否則 preview）。 */
  const fromRun = latestRun !== null && (latestRun.items?.length ?? 0) > 0

  const rows = useMemo<ShareRow[]>(() => {
    if (fromRun && latestRun) {
      return buildRowsFromRun(latestRun)
    }
    return buildPreviewRows(items, awards, decisions)
  }, [fromRun, latestRun, items, awards, decisions])

  const isLoading =
    caseQuery.isPending ||
    awardsQuery.isPending ||
    decisionsQuery.isPending ||
    latestRunQuery.isPending

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('shares.loadError')}
        {errDetail(caseQuery.error)}
      </p>
    )
  }

  const mismatchCount = rows.filter((r) => r.p + r.q + r.r !== r.k).length

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.shares')}</span>
        <h2 className="text-lg font-semibold">{t('shares.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('shares.subtitle')}</p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {fromRun ? `${t('shares.fromRun')} #${latestRun?.run_id}` : t('shares.preview')}
          </CardTitle>
          {mismatchCount > 0 ? (
            <Badge variant="destructive">{t('shares.mismatch')}</Badge>
          ) : rows.length > 0 ? (
            <Badge variant="secondary">{t('shares.ok')}</Badge>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('shares.ratioHint')}</p>
          <p className="text-xs text-muted-foreground">{t('shares.source')}</p>
          {mismatchCount > 0 ? (
            <p className="rounded-md border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
              {t('shares.mismatchHint')}
            </p>
          ) : null}
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('shares.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('shares.item')}</TableHead>
                    <TableHead className="text-right">{t('shares.j')}</TableHead>
                    <TableHead className="text-right">{t('shares.m')}</TableHead>
                    <TableHead className="text-right">{t('shares.k')}</TableHead>
                    <TableHead className="text-right">{t('shares.p')}</TableHead>
                    <TableHead className="text-right">{t('shares.q')}</TableHead>
                    <TableHead className="text-right">{t('shares.r')}</TableHead>
                    <TableHead className="text-right">{t('shares.sum')}</TableHead>
                    <TableHead>{t('shares.check')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const sum = row.p + row.q + row.r
                    const ok = sum === row.k
                    return (
                      <TableRow key={row.key} className={ok ? undefined : 'bg-destructive/5'}>
                        <TableCell className="font-medium">{row.label}</TableCell>
                        <TableCell className="text-right">¥{fmtNum(row.j)}</TableCell>
                        <TableCell className="text-right">¥{fmtNum(row.m)}</TableCell>
                        <TableCell className="text-right font-semibold">¥{fmtNum(row.k)}</TableCell>
                        <TableCell className="text-right">¥{fmtNum(row.p)}</TableCell>
                        <TableCell className="text-right">¥{fmtNum(row.q)}</TableCell>
                        <TableCell className="text-right">¥{fmtNum(row.r)}</TableCell>
                        <TableCell
                          className={`text-right ${ok ? '' : 'font-semibold text-destructive'}`}
                        >
                          ¥{fmtNum(sum)}
                        </TableCell>
                        <TableCell>
                          {ok ? (
                            <Badge variant="secondary">{t('shares.ok')}</Badge>
                          ) : (
                            <Badge variant="destructive">{t('shares.mismatch')}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** 由 P2 run 的 per-item j/m/p/q/r 構建展示列。 */
function buildRowsFromRun(run: SecondSettlementRunDto): ShareRow[] {
  const out: ShareRow[] = []
  for (const it of run.items ?? []) {
    const j = Number(it.j_auction_unit_price ?? '')
    const m = Number(it.m_member_unit_price ?? '')
    const p = Number(it.p_ef_share ?? '')
    const q = Number(it.q_member_share ?? '')
    const r = Number(it.r_venue_share ?? '')
    // 僅顯示已具備三方分配的品項（run 內 return/hold 不會有 p/q/r）。
    if (![j, m, p, q, r].every(Number.isFinite)) continue
    out.push({
      key: `run-item-${it.id}`,
      label: it.item_management_no,
      j,
      m,
      k: j - m,
      p,
      q,
      r,
    })
  }
  return out
}

/**
 * 無 P2 run 時的預覽：confirmed 落札単価（J）+ メンバー決策（M）+ 預設 20/40/40。
 * 僅納入 price_adjust（或無決策但 J≥M 的非調整品不顯示，因不需分配）。
 */
function buildPreviewRows(
  items: CaseItemDto[],
  awards: AwardDto[],
  decisions: MemberDecisionDto[],
): ShareRow[] {
  const awardByItem = new Map<number, string>()
  for (const a of awards) awardByItem.set(a.item_id, a.auction_unit_price)
  const decidedByItem = new Map<number, MemberDecisionDto>()
  for (const d of decisions) if (d.status === 'decided') decidedByItem.set(d.item_id, d)

  const out: ShareRow[] = []
  for (const item of items) {
    const auctionPrice = awardByItem.get(item.id)
    if (auctionPrice === undefined) continue
    const j = Number(auctionPrice)
    const decision = decidedByItem.get(item.id)
    // return / hold 不參與三方分配。
    if (decision && (decision.decision_type === 'return' || decision.decision_type === 'hold')) {
      continue
    }
    // M：price_adjust 用調整後単価；否則 item.min_unit_price。
    const mStr =
      decision?.decision_type === 'price_adjust' && decision.adjusted_member_unit_price
        ? decision.adjusted_member_unit_price
        : item.min_unit_price
    if (mStr === null || mStr === undefined) continue
    const m = Number(mStr)
    if (!Number.isFinite(j) || !Number.isFinite(m)) continue
    const k = j - m
    // 僅顯示 K>0 的可分配品項（落札 < 希望価才有 K 需分配）。
    if (k <= 0) continue
    const { p, q, r } = defaultShare(k)
    out.push({
      key: `preview-item-${item.id}`,
      label: `#${item.line_no} ${item.item_name}`,
      j,
      m,
      k,
      p,
      q,
      r,
    })
  }
  return out
}
